import { ArrowLeft, Building2, CheckCircle2, Database, Save, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { apiUrl } from '@/config/api';
import { cn } from '@/lib/utils';
import TenantSetupDialog from './TenantSetupDialog';

interface Corporation {
  id: string;
  name: string;
  nameEn?: string;
  invoiceNumber?: string;
  approvalDate?: string;
  phone?: string;
  fax?: string;
  addressId?: string;
  establishmentDate?: string;
  abolishedDate?: string;
  status: 'active' | 'suspended' | 'deleted';
  isContracted: 'true' | 'false';
  admissionsDbName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export default function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);

  const isNew = id === 'new';

  const [data, setData] = useState<Partial<Corporation>>({
    status: 'active',
    isContracted: 'false',
  });

  useEffect(() => {
    if (isNew || !id) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await fetch(apiUrl(`/api/tenants/${id}`));
        if (res.ok) {
          const json = await res.json();
          setData(json as Partial<Corporation>);
        } else {
          console.error('Failed to fetch corporation');
          alert(t('tenants.tenant.errors.notFound', 'Corporation not found'));
          navigate('/system/tenants');
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isNew, navigate, t]);

  /** Refresh data after successful DB provisioning */
  const handleSetupSuccess = (dbName: string) => {
    setData((prev) => ({ ...prev, admissionsDbName: dbName, isContracted: 'true' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      const url = isNew ? apiUrl('/api/tenants') : apiUrl(`/api/tenants/${id}`);

      const method = isNew ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: isNew ? data.id : undefined, // Only send ID on create
          name: data.name,
          nameEn: data.nameEn,
          invoiceNumber: data.invoiceNumber,
          establishmentDate: data.establishmentDate,
          phone: data.phone,
          fax: data.fax,
          status: data.status,
          isContracted: data.isContracted,
        }),
      });

      if (res.ok) {
        alert(t('common.success', 'Saved successfully'));
        navigate('/system/tenants');
      } else {
        const err = (await res.json()) as { error?: string };
        alert(t('tenants.tenant.errors.saveFailed', { error: err.error || 'Unknown error' }));
      }
    } catch (error) {
      console.error(error);
      alert(t('tenants.tenant.errors.saveError', 'Error saving'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && !isNew && !data.id) {
    return <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link
            to="/system/tenants"
            className="p-2 hover:bg-white rounded-full text-gray-500 transition-colors border border-transparent hover:border-gray-200 hover:shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="w-6 h-6 text-gray-700" />
              {isNew ? t('tenants.tenant.titleNew') : t('tenants.tenant.titleDetail', { name: data.name })}
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              {isNew ? t('tenants.tenant.descNew') : t('tenants.tenant.descDetail')}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white shadow-sm border border-gray-200 rounded-lg p-8 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="col-span-1">
            <label htmlFor="corp-id" className="block text-sm font-medium text-gray-700 mb-1">
              {t('tenants.tenant.fields.corporateNumber')}{' '}
              {isNew && (
                <span className="text-xs text-gray-500 font-normal ml-2">{t('common.optional', '(任意)')}</span>
              )}
            </label>
            <input
              id="corp-id"
              name="corp-id"
              type="text"
              className={cn('w-full border-gray-300 rounded-md shadow-sm px-3 py-2 border', !isNew && 'bg-gray-50')}
              value={data.id || ''}
              readOnly={!isNew}
              onChange={(e) => setData({ ...data, id: e.target.value })}
              placeholder={isNew ? t('tenants.tenant.placeholders.autoNumber', 'Auto-generated (optional)') : ''}
              maxLength={13}
            />
          </div>

          <div className="col-span-1">
            <label htmlFor="corp-status" className="block text-sm font-medium text-gray-700 mb-1">
              {t('tenants.tenant.fields.status')}
            </label>
            <select
              id="corp-status"
              name="corp-status"
              className="w-full border-gray-300 rounded-md shadow-sm px-3 py-2 border"
              value={data.status}
              onChange={(e) => setData({ ...data, status: e.target.value as Corporation['status'] })}
            >
              <option value="active">{t('tenants.tenant.statuses.active', 'Active')}</option>
              <option value="suspended">{t('tenants.tenant.statuses.suspended', 'Suspended')}</option>
              <option value="deleted">{t('tenants.tenant.statuses.deleted', 'Deleted')}</option>
            </select>
          </div>
          <div className="col-span-1">
            <label htmlFor="corp-contract" className="block text-sm font-medium text-gray-700 mb-1">
              {t('tenants.tenant.fields.contractStatus')}
            </label>
            <select
              id="corp-contract"
              name="corp-contract"
              className={cn(
                'w-full rounded-md shadow-sm px-3 py-2 border font-bold',
                data.isContracted === 'true'
                  ? 'border-green-300 bg-green-50 text-green-800'
                  : 'border-gray-300 bg-white text-gray-800',
              )}
              value={data.isContracted}
              onChange={(e) => setData({ ...data, isContracted: e.target.value as Corporation['isContracted'] })}
            >
              <option value="true">{t('tenants.tenant.fields.contractStatusOptions.partner')}</option>
              <option value="false">{t('tenants.tenant.fields.contractStatusOptions.master')}</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">{t('tenants.tenant.fields.contractStatusHelp')}</p>
          </div>

          <div className="col-span-2">
            <label htmlFor="corp-name" className="block text-sm font-medium text-gray-700 mb-1">
              {t('tenants.tenant.fields.nameJa')} <span className="text-red-500">*</span>
            </label>
            <input
              id="corp-name"
              name="corp-name"
              type="text"
              required
              className="w-full border-gray-300 rounded-md shadow-sm px-3 py-2 border"
              value={data.name || ''}
              onChange={(e) => setData({ ...data, name: e.target.value })}
            />
          </div>

          <div className="col-span-2">
            <label htmlFor="corp-name-en" className="block text-sm font-medium text-gray-700 mb-1">
              {t('tenants.tenant.fields.nameEn')}
            </label>
            <input
              id="corp-name-en"
              name="corp-name-en"
              type="text"
              className="w-full border-gray-300 rounded-md shadow-sm px-3 py-2 border"
              value={data.nameEn || ''}
              onChange={(e) => setData({ ...data, nameEn: e.target.value })}
            />
          </div>

          <div className="col-span-1">
            <label htmlFor="corp-invoice" className="block text-sm font-medium text-gray-700 mb-1">
              {t('tenants.tenant.fields.invoiceNumber')}
            </label>
            <input
              id="corp-invoice"
              name="corp-invoice"
              type="text"
              className="w-full border-gray-300 rounded-md shadow-sm px-3 py-2 border"
              value={data.invoiceNumber || ''}
              onChange={(e) => setData({ ...data, invoiceNumber: e.target.value })}
              placeholder="T1234567890123"
            />
          </div>

          <div className="col-span-1">
            <label htmlFor="corp-approval-date" className="block text-sm font-medium text-gray-700 mb-1">
              {t('tenants.tenant.fields.approvalDate', 'Approval Date')}
            </label>
            <input
              id="corp-approval-date"
              name="corp-approval-date"
              type="date"
              className="w-full border-gray-300 rounded-md shadow-sm px-3 py-2 border bg-gray-50"
              value={data.approvalDate || ''}
              readOnly
            />
          </div>

          <div className="col-span-1">
            <label htmlFor="corp-phone" className="block text-sm font-medium text-gray-700 mb-1">
              {t('tenants.tenant.fields.phone', 'Phone Number')}
            </label>
            <input
              id="corp-phone"
              name="corp-phone"
              type="tel"
              className="w-full border-gray-300 rounded-md shadow-sm px-3 py-2 border"
              value={data.phone || ''}
              onChange={(e) => setData({ ...data, phone: e.target.value })}
              placeholder="03-1234-5678"
            />
          </div>

          <div className="col-span-1">
            <label htmlFor="corp-fax" className="block text-sm font-medium text-gray-700 mb-1">
              {t('tenants.tenant.fields.fax', 'Fax Number')}
            </label>
            <input
              id="corp-fax"
              name="corp-fax"
              type="tel"
              className="w-full border-gray-300 rounded-md shadow-sm px-3 py-2 border"
              value={data.fax || ''}
              onChange={(e) => setData({ ...data, fax: e.target.value })}
              placeholder="03-1234-5679"
            />
          </div>

          <div className="col-span-1">
            <label htmlFor="corp-est-date" className="block text-sm font-medium text-gray-700 mb-1">
              {t('tenants.tenant.fields.establishmentDate')}
            </label>
            <input
              id="corp-est-date"
              name="corp-est-date"
              type="date"
              className="w-full border-gray-300 rounded-md shadow-sm px-3 py-2 border"
              value={data.establishmentDate || ''}
              onChange={(e) => setData({ ...data, establishmentDate: e.target.value })}
            />
          </div>
        </div>

        {data.isContracted === 'true' && (
          <>
            {/* Initial Setup Status Section */}
            <div className="pt-8 border-t">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Database className="w-5 h-5" />
                {t('tenants.tenant.initialSetup.title', 'Initial Setup Status')}
              </h2>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    {t('tenants.tenant.initialSetup.dbProvisioning', 'Tenant DB Provisioning')}
                  </span>
                  {data.admissionsDbName ? (
                    <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                      <CheckCircle2 className="w-4 h-4" />
                      {t('tenants.tenant.initialSetup.completed', 'Completed')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-orange-600 text-sm font-medium">
                      <XCircle className="w-4 h-4" />
                      {t('tenants.tenant.initialSetup.pending', 'Pending')}
                    </span>
                  )}
                </div>
                {data.admissionsDbName && (
                  <div className="text-xs text-gray-500 font-mono bg-white px-2 py-1 rounded border">
                    DB: {data.admissionsDbName}
                  </div>
                )}
                {!data.admissionsDbName && (
                  <div className="pt-2">
                    <Button type="button" size="sm" onClick={() => setSetupDialogOpen(true)}>
                      {t('tenants.tenant.initialSetup.startButton', 'Start Initial Setup')}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Un-setup Institutions Section */}
            <div className="pt-8 border-t">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                {t('tenants.tenant.unsetupInstitutions.title', 'Un-setup Institutions')}
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                {t(
                  'tenants.tenant.unsetupInstitutions.viewDescription',
                  'View the list of institutions for this tenant that have not been set up yet.',
                )}
              </p>
              <Link to={`/system/tenants/${id}/unsetup-institutions`}>
                <Button variant="outline">
                  {t('tenants.tenant.unsetupInstitutions.viewButton', 'View Un-setup Institutions')}
                </Button>
              </Link>
            </div>
          </>
        )}

        <div className="pt-8 border-t flex justify-end gap-3">
          <Link
            to="/system/tenants"
            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('common.cancel')}
          </Link>
          <button
            type="submit"
            className="px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-2 shadow-sm transition-colors"
            disabled={loading}
          >
            <Save className="w-4 h-4" />
            {loading ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </form>

      {/* Tenant Setup Wizard Dialog */}
      <TenantSetupDialog
        open={setupDialogOpen}
        onOpenChange={setSetupDialogOpen}
        corporateId={id || ''}
        corporateName={data.name || ''}
        onSuccess={handleSetupSuccess}
      />
    </div>
  );
}
